import React, { useState } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { useAddRoute, useRemoveRoute, useRoutes, useUpdateRoute } from '../hooks/useTravelBuddy';
import { ROUTE_MODE_LABELS } from '../lib/categoryLabels';
import type { RouteMode, TripRoute } from '../types';
import * as T from '../ui/inlineTheme';

const modes: RouteMode[] = ['flight', 'train', 'bus', 'ferry', 'car', 'other'];

function formatWhen(iso: string | null): string {
  if (!iso?.trim()) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
}

function toDatetimeLocalValue(iso: string | null): string {
  if (!iso?.trim()) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    const m = iso.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})/);
    return m ? m[1] : '';
  }
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const removeBtn: React.CSSProperties = {
  padding: 8,
  border: 'none',
  borderRadius: 8,
  background: 'transparent',
  color: '#9CA3AF',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  flexShrink: 0,
  alignSelf: 'flex-start',
};

const routeRow: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 12,
  borderRadius: 14,
  border: '1px solid #3f3f46',
  backgroundColor: 'rgba(24, 24, 27, 0.7)',
  padding: 14,
  boxSizing: 'border-box',
};

const iconCol: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  flexShrink: 0,
};

function RouteCard({
  r,
  tripId,
}: {
  r: TripRoute;
  tripId: string;
}) {
  const remove = useRemoveRoute(tripId);
  const update = useUpdateRoute(tripId);
  const [editing, setEditing] = useState(false);
  const [mode, setMode] = useState<RouteMode>(r.mode);
  const [origin, setOrigin] = useState(r.origin);
  const [destination, setDestination] = useState(r.destination);
  const [departAt, setDepartAt] = useState(toDatetimeLocalValue(r.depart_at));
  const [arriveAt, setArriveAt] = useState(toDatetimeLocalValue(r.arrive_at));
  const [notes, setNotes] = useState(r.notes);
  const [flightNo, setFlightNo] = useState(r.flight_number || '');

  const startEdit = () => {
    setMode(r.mode);
    setOrigin(r.origin);
    setDestination(r.destination);
    setDepartAt(toDatetimeLocalValue(r.depart_at));
    setArriveAt(toDatetimeLocalValue(r.arrive_at));
    setNotes(r.notes);
    setFlightNo(r.flight_number || '');
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
  };

  const saveEdit = () => {
    if (!origin.trim() || !destination.trim()) return;
    update.mutate(
      {
        routeId: r.id,
        patch: {
          mode,
          origin: origin.trim(),
          destination: destination.trim(),
          depart_at: departAt.trim() || null,
          arrive_at: arriveAt.trim() || null,
          notes: notes.trim(),
          flight_number: flightNo.trim() || null,
        },
      },
      { onSuccess: () => setEditing(false) }
    );
  };

  const busy = remove.isPending || update.isPending;

  if (editing) {
    return (
      <li style={routeRow}>
        <div style={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <p style={{ ...T.labelSmall, margin: 0 }}>Edit route</p>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontFamily: T.font, fontSize: 13, color: '#a1a1aa' }}>Type</span>
            <select style={T.select} value={mode} onChange={(e) => setMode(e.target.value as RouteMode)}>
              {modes.map((m) => (
                <option key={m} value={m}>
                  {ROUTE_MODE_LABELS[m]}
                </option>
              ))}
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontFamily: T.font, fontSize: 13, color: '#a1a1aa' }}>From</span>
            <input
              style={T.input}
              value={origin}
              onChange={(e) => setOrigin(e.target.value)}
              autoComplete="off"
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontFamily: T.font, fontSize: 13, color: '#a1a1aa' }}>To</span>
            <input
              style={T.input}
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
              autoComplete="off"
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontFamily: T.font, fontSize: 13, color: '#a1a1aa' }}>Flight / train no. (optional)</span>
            <input
              style={T.input}
              value={flightNo}
              onChange={(e) => setFlightNo(e.target.value)}
              placeholder="e.g. AY123"
              autoComplete="off"
            />
          </label>
          <div style={{ ...T.flexRowGap, alignItems: 'stretch' }}>
            <label style={{ flex: 1, minWidth: 140, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontFamily: T.font, fontSize: 13, color: '#a1a1aa' }}>Depart</span>
              <input
                style={T.input}
                type="datetime-local"
                value={departAt}
                onChange={(e) => setDepartAt(e.target.value)}
              />
            </label>
            <label style={{ flex: 1, minWidth: 140, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontFamily: T.font, fontSize: 13, color: '#a1a1aa' }}>Arrive</span>
              <input
                style={T.input}
                type="datetime-local"
                value={arriveAt}
                onChange={(e) => setArriveAt(e.target.value)}
              />
            </label>
          </div>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontFamily: T.font, fontSize: 13, color: '#a1a1aa' }}>Notes</span>
            <textarea
              style={T.textarea}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
            />
          </label>
          <div style={{ ...T.flexRowGap, marginTop: 4 }}>
            <button
              type="button"
              className="sf-btn-primary"
              style={{ ...T.btnPrimary, flex: 1, justifyContent: 'center' }}
              disabled={busy || !origin.trim() || !destination.trim()}
              onClick={saveEdit}
            >
              Save
            </button>
            <button
              type="button"
              className="sf-btn-secondary"
              style={{
                ...T.btnPrimary,
                flex: 1,
                justifyContent: 'center',
                background: 'transparent',
                border: '1px solid #2A2A2A',
                color: '#E5E7EB',
              }}
              disabled={busy}
              onClick={cancelEdit}
            >
              Cancel
            </button>
          </div>
        </div>
      </li>
    );
  }

  return (
    <li style={routeRow}>
      <div style={{ minWidth: 0, flex: 1 }}>
        <p style={{ margin: 0, fontFamily: T.font, fontSize: 13, fontWeight: 700, color: '#3A8DFF' }}>
          {ROUTE_MODE_LABELS[r.mode]}
        </p>
        <p style={{ margin: '8px 0 0', fontFamily: T.font, fontSize: 17, fontWeight: 600, color: '#E5E7EB' }}>
          {r.origin} → {r.destination}
          {r.flight_number ? (
            <span style={{ fontWeight: 500, color: '#a1a1aa', fontSize: 15 }}> · {r.flight_number}</span>
          ) : null}
        </p>
        {r.source_receipt_id ? (
          <p style={{ margin: '6px 0 0', fontFamily: T.font, fontSize: 12, color: '#9CA3AF' }}>
            From travel document
          </p>
        ) : null}
        <p style={{ margin: '6px 0 0', fontFamily: T.font, fontSize: 14, color: '#a1a1aa' }}>
          Depart {formatWhen(r.depart_at)} · Arrive {formatWhen(r.arrive_at)}
        </p>
        {r.notes ? (
          <p style={{ margin: '8px 0 0', fontFamily: T.font, fontSize: 14, color: '#d4d4d8', lineHeight: 1.45 }}>
            {r.notes}
          </p>
        ) : null}
      </div>
      <div style={iconCol}>
        <button
          type="button"
          data-tb-icon="true"
          title="Edit route"
          style={{
            ...removeBtn,
            color: '#a5b4fc',
            opacity: busy ? 0.45 : 1,
            cursor: busy ? 'wait' : 'pointer',
          }}
          aria-label="Edit route"
          disabled={busy}
          onClick={startEdit}
        >
          <Pencil size={18} strokeWidth={2} />
        </button>
        <button
          type="button"
          data-tb-icon="true"
          style={{
            ...removeBtn,
            opacity: busy ? 0.45 : 1,
            cursor: busy ? 'wait' : 'pointer',
          }}
          aria-label="Remove route"
          disabled={busy}
          onClick={() => remove.mutate(r.id)}
        >
          <Trash2 size={18} strokeWidth={2} />
        </button>
      </div>
    </li>
  );
}

export function RoutesTab({ tripId }: { tripId: string }) {
  const { data: routes = [], isLoading } = useRoutes(tripId);
  const add = useAddRoute(tripId);

  const [mode, setMode] = useState<RouteMode>('flight');
  const [origin, setOrigin] = useState('');
  const [destination, setDestination] = useState('');
  const [departAt, setDepartAt] = useState('');
  const [arriveAt, setArriveAt] = useState('');
  const [notes, setNotes] = useState('');
  const [flightNo, setFlightNo] = useState('');

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!origin.trim() || !destination.trim()) return;
    add.mutate(
      {
        mode,
        origin: origin.trim(),
        destination: destination.trim(),
        depart_at: departAt.trim() || null,
        arrive_at: arriveAt.trim() || null,
        notes: notes.trim(),
        flight_number: flightNo.trim() || null,
      },
      {
        onSuccess: () => {
          setOrigin('');
          setDestination('');
          setDepartAt('');
          setArriveAt('');
          setNotes('');
          setFlightNo('');
        },
      }
    );
  };

  return (
    <div style={T.flexGap}>
      <p style={{ ...T.bodyMuted, margin: 0 }}>
        Add legs manually, or upload boarding passes and travel-agent PDFs under{' '}
        <strong style={{ color: '#d4d4d8' }}>Travel documents</strong> — we add matching legs here when we can read
        Origin / Destination / times from the file (deduplicated automatically). Edit any row with the pencil icon.
      </p>

      <form onSubmit={onSubmit} style={{ ...T.cardSolid, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <p style={{ ...T.labelSmall, marginBottom: 0 }}>New route</p>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontFamily: T.font, fontSize: 13, color: '#a1a1aa' }}>Type</span>
          <select style={T.select} value={mode} onChange={(e) => setMode(e.target.value as RouteMode)}>
            {modes.map((m) => (
              <option key={m} value={m}>
                {ROUTE_MODE_LABELS[m]}
              </option>
            ))}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontFamily: T.font, fontSize: 13, color: '#a1a1aa' }}>From</span>
          <input
            style={T.input}
            value={origin}
            onChange={(e) => setOrigin(e.target.value)}
            placeholder="e.g. Helsinki"
            autoComplete="off"
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontFamily: T.font, fontSize: 13, color: '#a1a1aa' }}>To</span>
          <input
            style={T.input}
            value={destination}
            onChange={(e) => setDestination(e.target.value)}
            placeholder="e.g. Oslo"
            autoComplete="off"
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontFamily: T.font, fontSize: 13, color: '#a1a1aa' }}>Flight / train no. (optional)</span>
          <input
            style={T.input}
            value={flightNo}
            onChange={(e) => setFlightNo(e.target.value)}
            placeholder="e.g. AY123"
            autoComplete="off"
          />
        </label>
        <div style={{ ...T.flexRowGap, alignItems: 'stretch' }}>
          <label style={{ flex: 1, minWidth: 140, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontFamily: T.font, fontSize: 13, color: '#a1a1aa' }}>Depart</span>
            <input
              style={T.input}
              type="datetime-local"
              value={departAt}
              onChange={(e) => setDepartAt(e.target.value)}
            />
          </label>
          <label style={{ flex: 1, minWidth: 140, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontFamily: T.font, fontSize: 13, color: '#a1a1aa' }}>Arrive</span>
            <input
              style={T.input}
              type="datetime-local"
              value={arriveAt}
              onChange={(e) => setArriveAt(e.target.value)}
            />
          </label>
        </div>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontFamily: T.font, fontSize: 13, color: '#a1a1aa' }}>Notes (optional)</span>
          <textarea
            style={T.textarea}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Seat, booking ref…"
            rows={3}
          />
        </label>
        <button
          type="submit"
          className="sf-btn-primary"
          style={{ ...T.btnPrimary, width: '100%', justifyContent: 'center' }}
          disabled={add.isPending}
        >
          Add route
        </button>
      </form>

      <div>
        <h3 style={{ ...T.labelSmall, marginBottom: 10 }}>Your routes</h3>
        {isLoading && <p style={T.bodyMuted}>Loading…</p>}
        {!isLoading && routes.length === 0 && (
          <p style={{ ...T.bodyMuted, margin: 0 }}>No routes yet. Add one above.</p>
        )}
        <ul style={{ listStyle: 'none', margin: '12px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {routes.map((r) => (
            <RouteCard key={r.id} r={r} tripId={tripId} />
          ))}
        </ul>
      </div>
    </div>
  );
}
